/** @jsx jsx */
import { jsx, Themed, useThemeUI } from 'theme-ui'
import React from 'react'
import classNames from 'classnames'
import { useExperiences, useDataSelector, useMeta } from './selectors'
import { isObject, isEmpty } from '../../utils/obj'
import { contentId } from '../../utils/contentful'
import { Experiment, Fallback } from 'react-chansey'
import { EVEntry as EVEntry_ } from './entry'
export const EVEntry = EVEntry_

export { RichText } from './entry/rich-text'

const getGutterSize = (theme) => theme?.layout && theme?.layout['flex-grid'].sizes?.gutter
const uncastArrayIfSingle = (arr) => (arr.length === 1 ? arr[0] : arr)
const castArray = (valueOrArray) => [].concat(valueOrArray) // wiiilllllson
const getValueFromSize = (theme, size) => (typeof size === 'number' ? size : theme.space[size] || 0)
const getVerticalGutterSize = (theme) =>
  theme?.layout && theme?.layout['flex-grid'].sizes?.verticalGutter
const getContainerSize = (theme) =>
  (theme?.layout && theme?.layout['flex-grid']?.container?.maxWidth) || 1200
const getHalf = (value) => value / 2
const toPx = (value) => `${value}px`
const getValueFromBreakpointIndex = (arr, index) => {
  if (arr[index] !== undefined) return arr[index]
  return arr[arr.length - 1]
}

export const isChildOfContainerContext = React.createContext()
export const columnSizeContext = React.createContext()

export const Container = ({ children, cols, span, className = '', ...props }) => {
  const classes = classNames('flex-grid-container', className)
  const colsArray = castArray(cols)
  const spanArray = castArray(span)
  return (
    <div
      sx={{
        mx: 'auto',
        boxSizing: 'border-box',
        px: getGutterSize,
        maxWidth: (theme) =>
          uncastArrayIfSingle(
            castArray(getContainerSize(theme)).map(
              (maxWidth, i) =>
                maxWidth * (colsArray[i] && spanArray[i] ? spanArray[i] / colsArray[i] : 1),
            ),
          ),
        variant: 'compounds.container.flex',
      }}
      {...props}
      className={classes}
    >
      <isChildOfContainerContext.Provider value={true}>
        {children}
      </isChildOfContainerContext.Provider>
    </div>
  )
}

export const Row = ({
  children,
  cols = 12,
  direction = ['column', 'row', 'row'],
  wrap = true,
  ...props
}) => {
  const childrenArray = Array.isArray(children) ? children : [children]
  return (
    <div
      sx={{
        mx: (theme) =>
          uncastArrayIfSingle(
            castArray(getGutterSize(theme))
              .map((value) => getValueFromSize(theme, value))
              .map(getHalf)
              .map((gutterValue) => `${gutterValue * -1}`)
              .map(toPx),
          ),
        display: 'flex',
        flexDirection: direction,
        flexWrap: wrap ? 'wrap' : 'nowrap',
      }}
      {...props}
    >
      {childrenArray.map((child, key) =>
        React.isValidElement(child) ? React.cloneElement(child, { cols, key }) : child,
      )}
    </div>
  )
}

export const Col = ({ children, cols = 12, span, offset, ...props }) => {
  const { theme } = useThemeUI()
  if (Array.isArray(span) && !Array.isArray(cols)) {
    const colsNumber = cols
    cols = []
    for (let i = 0; i < span.length; i++) {
      cols.push(colsNumber)
    }
  }

  const columnValues = castArray(cols).map((colValue, index) => {
    const gutterValue = toPx(
      getValueFromBreakpointIndex(
        castArray(getGutterSize(theme)).map((value) => getValueFromSize(theme, value)),
        index,
      ),
    )
    const currentSpan = getValueFromBreakpointIndex(castArray(span), index)

    return { currentSpan, colValue, gutterValue }
  })

  const width = uncastArrayIfSingle(
    columnValues.map(({ currentSpan, colValue, gutterValue }) => {
      return `calc(${(currentSpan / colValue) * 100}% - ${gutterValue})`
    }),
  )

  return (
    <columnSizeContext.Provider value={columnValues}>
      <div
        sx={{
          boxSizing: 'border-box',
          mr: (theme) =>
            uncastArrayIfSingle(
              castArray(getGutterSize(theme))
                .map((value) => getValueFromSize(theme, value))
                .map(getHalf)
                .map(toPx),
            ),
          mb: getVerticalGutterSize,
          flexGrow: span ? 0 : 1,
          flexShrink: 0,
          flexBasis: `auto`,
          ...(span
            ? {
                width,
              }
            : {}),
          ...(offset
            ? {
                ml: (theme) =>
                  uncastArrayIfSingle(
                    castArray(cols).map((colValue, index) => {
                      const gutterValue = getValueFromBreakpointIndex(
                        castArray(getGutterSize(theme)).map((value) =>
                          getValueFromSize(theme, value),
                        ),
                        index,
                      )

                      const offsetValue = getValueFromBreakpointIndex(
                        castArray(offset).map((value) => getValueFromSize(theme, value)),
                        index,
                      )

                      return `calc(${(offsetValue / colValue) * 100}% + ${gutterValue / 2}px)`
                    }),
                  ),
              }
            : {
                ml: (theme) =>
                  uncastArrayIfSingle(
                    castArray(getGutterSize(theme))
                      .map((value) => getValueFromSize(theme, value))
                      .map(getHalf)
                      .map(toPx),
                  ),
              }),
        }}
        {...props}
      >
        {children}
      </div>
    </columnSizeContext.Provider>
  )
}

const NA_FLAG_ID = 'not-applicable' // Default not applicable flag ID

const selectEntity = ({
  experienceFlag,
  experiences = {},
  entityMap = {},
  experienceList = [],
  entityList = [],
  fullPageTest = false,
} = {}) => {
  const experience = experiences[experienceFlag]

  // This condition is for the URL contentType with multiple variants
  // e.g. full pages tests
  if (fullPageTest) {
    return entityList[experience?.cohort || 0]
  }

  const experienceValue = experience?.value
  // Experience mapping based on mapping experienceFlag values to contentful
  // entity ids
  if (isObject(entityMap) && !isEmpty(entityMap)) {
    const entityId = entityMap[experienceValue] || entityMap[NA_FLAG_ID]
    return entityId ? { id: entityId } : null
  }

  // Legacy experience mapping based on indexes
  const entityIndex = experienceList.findIndex((experience) => experience === experienceValue)
  return entityList[entityIndex] || entityList[0] || null
}

export const ExperienceEntitySelector = (props) => {
  const experiences = useExperiences()
  const selectedEntity = selectEntity({ ...props, experiences })
  return (
    <Experiment id={props.experienceFlag}>
      <If condition={selectedEntity}>
        <Fallback>
          <EVEntry_ {...selectedEntity} />
        </Fallback>
      </If>
    </Experiment>
  )
}

const sameHostParams = (host, href = '') => {
  try {
    const sameHost = host === new URL(href).host
    return sameHost ? {} : { target: '_blank', rel: 'noopener noreferrer nofollow' }
  } catch (e) {
    return {}
  }
}

export const Link = ({ children, className, href, ...props }) => {
  const host = useDataSelector(['location', 'host'])
  const hostParams = sameHostParams(host, href)
  const vertical = useMeta(['appName'], 'eevee')
  const product_id = props?.product_id || href?.match(/\/clk\/([^/]+)/)?.[1]

  return (
    <Themed.a
      href={href}
      className={className}
      data-nerd
      data-nerd-type='element'
      data-nerd-clickable
      data-nerd-clickable-target
      data-nerd-props={JSON.stringify({
        org_vertical: vertical,
        element_name: props?.name,
        contentful_id: contentId(props),
        ...(product_id ? { product_id } : {}),
      })}
      {...props}
      {...hostParams}
    >
      {children}
    </Themed.a>
  )
}

export const LinkWithChevron = ({ children, variant = '', href = '', className, ...props }) => {
  const host = useDataSelector(['location', 'host'])
  const sameHost = sameHostParams(host, href)

  const vertical = useMeta(['appName'], 'eevee')
  return (
    <Themed.a
      className={variant ? `${variant} ${className}` : className}
      href={href}
      data-nerd
      data-nerd-type='element'
      data-nerd-clickable
      data-nerd-clickable-target
      data-nerd-props={JSON.stringify({
        org_vertical: vertical,
        element_name: props?.name,
        contentful_id: contentId(props),
      })}
      {...props}
      {...sameHost}
      sx={{ ...(variant ? { variant: `styles.a.${variant}` } : {}) }}
    >
      <span sx={{ variant: 'elements.link-with-chevron' }}>{children}</span>
    </Themed.a>
  )
}

export const useContainerSize = () => {
  const { theme } = useThemeUI()
  return getContainerSize(theme)
}
